import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { ReviewIcon } from "../../../shared/components/icons";
import { WordDocBanner } from "../../document/components/WordDocBanner";
import type { WordDocumentContext } from "../../document/types";
import styles from "./ReviewModule.module.css";

interface ReviewModuleProps {
  readonly onBack?: () => void;
  readonly host: typeof Office.HostType[keyof typeof Office.HostType] | null;
  readonly doc: WordDocumentContext;
  readonly docLoading: boolean;
  readonly onRefreshDoc: () => void;
  readonly onPromptInChat?: (prompt: string) => void;
}

const QUICK_PROMPTS: Array<{ id: string; label: string; prompt: string }> = [
  { id: "summary", label: "Summarise document", prompt: "Summarise the key points of this document in 5 bullets." },
  { id: "formatting", label: "Review formatting", prompt: "Review the heading hierarchy, style consistency, and any formatting issues in this document." },
  { id: "risks", label: "Flag risks & clauses", prompt: "Identify any risky language, ambiguous clauses, or compliance concerns in this document." },
  { id: "tone", label: "Polish tone", prompt: "Suggest edits to improve the tone and clarity of this document for a professional audience." },
];

export function ReviewModule({
  onBack,
  host,
  doc,
  docLoading,
  onRefreshDoc,
  onPromptInChat,
}: Readonly<ReviewModuleProps>) {
  const isWord = host === Office.HostType.Word;

  return (
    <div className={styles.module}>
      <ModuleHeader
        title="Review"
        subtitle="AI document analysis"
        icon={ReviewIcon}
        accent="review"
        onBack={onBack}
      />

      <div className={styles.body}>
        {!isWord && (
          <div className={styles.notice}>
            <p className={styles.noticeTitle}>Open this add-in in Word</p>
            <p className={styles.noticeMsg}>
              The Review module analyses the current Word document. Open a Word document and re-launch
              AiVA to use this feature.
            </p>
          </div>
        )}

        {isWord && (
          <>
            <WordDocBanner doc={doc} loading={docLoading} onRefresh={onRefreshDoc} />

            <div className={styles.section}>
              <p className={styles.sectionTitle}>Quick analysis</p>
              <p className={styles.help}>
                Run any of these analyses against the current document. The document text is sent with
                your message to AiVA so the answer is grounded in the actual content.
              </p>
              <div className={styles.quickGrid}>
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    className={styles.quickBtn}
                    onClick={() => onPromptInChat?.(q.prompt)}
                    disabled={!onPromptInChat || doc.wordCount === 0}
                    title={q.prompt}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              {doc.wordCount === 0 && (
                <p className={styles.help}>
                  This document looks empty. Add content and click Refresh.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
